import React from 'react';
import { BhiDialog } from 'src/UIKit/components/Dialog';
import {
  Box,
  Button,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, PhoneAndroid as PhoneAndroidIcon, ScreenRotation as ScreenRotationIcon } from '@mui/icons-material';
import { useMobileRotation } from 'src/contexts/MobileRotationContext';

export const MobileRotationModal: React.FC = () => {
  const { isRotationModalOpen, dismissPermanently } = useMobileRotation();
  const theme = useTheme();


  const handleClose = () => {
    dismissPermanently();
  };

  const handleDismiss = () => {
    dismissPermanently();
  };

  return (
    <BhiDialog
      open={isRotationModalOpen}
      onClose={handleClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'grey.100',
          margin: 0,
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '100vw',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: { xs: 16, sm: 20 },
            right: { xs: 16, sm: 20 },
            color: theme.palette.primary.main,
            zIndex: 1,
            padding: { xs: '8px', sm: '12px' },
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            padding: { xs: 2, sm: 3 },
            textAlign: 'center',
            maxWidth: '100vw',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            alignItems="center"
            sx={{ 
              mb: { xs: 3, sm: 4 }, 
              color: theme.palette.primary.main,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <PhoneAndroidIcon sx={{ fontSize: { xs: 36, sm: 48 } }} />
            <ScreenRotationIcon sx={{ fontSize: { xs: 36, sm: 48 }, transform: 'rotate(90deg)' }} />
          </Stack>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 'fontWeightBold',
              color: theme.palette.primary.main,
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              lineHeight: 1.2,
              wordBreak: 'break-word',
            }}
          >
            Want a better experience?
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.primary,
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
              lineHeight: 1.3,
            }}
          >
            Rotate your device.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: { xs: 3, sm: 4 },
              lineHeight: 1.6,
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              px: { xs: 1, sm: 2 },
              maxWidth: '100%',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {`We've noticed youre viewing on a mobile device `}
            <br />
            {`We recommend that you `}
            <Typography
              component="span"
              sx={{
                fontWeight: 'fontWeightBold',
                color: theme.palette.text.primary,
              }}
            >
              rotate your device horizontally
            </Typography>
            {` for the best experience.`}
          </Typography>

          <Button
            onClick={handleDismiss}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'common.white',
              borderRadius: '25px',
              padding: { xs: '10px 32px', sm: '12px 48px' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 'fontWeightBold',
              boxShadow: 'none',
              minWidth: { xs: '120px', sm: 'auto' },
              maxWidth: 'calc(100vw - 32px)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: 'none',
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </BhiDialog>
  );
};

export default MobileRotationModal;
